import { useMemo, useState } from "react";

import { CardsGallery } from "@widgets/cards-gallery";
import { useViewport } from "@widgets/device-info";
import { Section } from "@widgets/section";

import type { SliderSectionBase } from "@shared/content-types/schemas/sections.schema";
import { ButtonWithIcon } from "@shared/ui/button-with-icon";
import { Container } from "@shared/ui/container";
import { SimpleTabs } from "@shared/ui/simple-tabs";
import { Body, Heading } from "@shared/ui/typography";

import s from "./slider-section.module.scss";

type SliderSectionComponentProps = {
  sliders: SliderSectionBase["sliders"];
};

export const SliderSection = ({ sliders }: SliderSectionComponentProps) => {
  const [activeSlider, setActiveSlider] = useState(0);
  const { isDesktop, isMobile } = useViewport();

  const isMultipleSliders = sliders.length > 1;

  const tabs = useMemo(() => {
    return sliders.map((slider, index) => ({
      id: index,
      label: slider.subtitle,
    }));
  }, [sliders]);

  const handleSliderChange = (value: string | number) => {
    setActiveSlider(Number(value));
  };

  const button = useMemo(() => {
    return (
      sliders[activeSlider] && (
        <ButtonWithIcon
          className={s.button}
          iconName={sliders[activeSlider].buttonIcon?.icon?.name || "plus"}
          iconFillColor={
            sliders[activeSlider].buttonIcon?.fillColor || "cadmium"
          }
          iconStrokeColor={
            sliders[activeSlider].buttonIcon?.strokeColor || "white"
          }
          href={sliders[activeSlider].button?.link}
          variant="primary-dark"
        >
          {sliders[activeSlider].button?.text}
        </ButtonWithIcon>
      )
    );
  }, [activeSlider]);

  if (!sliders[activeSlider]) {
    return null;
  }

  return (
    <Section className={s.root}>
      <Container className={s.wrapper}>
        <div className={s.info}>
          <div className={s.nav}>
            {isMultipleSliders ? (
              <SimpleTabs
                items={tabs}
                activeItemId={activeSlider}
                onTabClick={handleSliderChange}
                className={s.filters}
                fullWidth={!isDesktop}
              />
            ) : (
              <Body
                size="uppercase-small"
                weight="semiBold"
                className={s.subtitle}
              >
                {sliders[activeSlider].subtitle}
              </Body>
            )}
            <p className={s.title}>{sliders[activeSlider].title}</p>
          </div>
          <div
            className={s.moreInfo}
            data-description-empty={!sliders[activeSlider].description}
          >
            <Body className={s.description}>
              {sliders[activeSlider].description}
            </Body>
            {sliders[activeSlider]?.button && button && (
              <div className={s.buttonWrapper}>{button}</div>
            )}
          </div>
        </div>
        <div className={s.slider}>
          <CardsGallery
            items={sliders[activeSlider].sliderCards}
            isMobile={!!isMobile}
          />
        </div>
        {sliders[activeSlider]?.button && button && (
          <div className={s.bottomButton}>{button}</div>
        )}
      </Container>
    </Section>
  );
};
